import { ExportPdfDropdownIcon } from '@/Components/Common/Icons';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { Dropdown, Spin, message } from 'antd';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { DASHBOARD_REPORT_TYPES } from '@/utilities/helpers/fileHelper';

/**
 * Split-button Export PDF for the Account Dashboard.
 *
 * - Clicking the pill/text area  → immediately exports with current selection
 *   (Summary is checked by default; Detail starts unchecked).
 * - Clicking the chevron area only → opens / closes the preference dropdown.
 * - The dropdown has two checkboxes (no confirm button).
 *   Checking / unchecking sets the preference; the panel stays open until
 *   the user clicks outside or the chevron again.
 * - Selection persists while the component is mounted.
 */
// The chevron in ExportPdfDropdownIcon (178 px wide) starts at x ≈ 152 px.
const CHEVRON_X = 152;

const DashboardExportButton = ({ onExport, disabled }) => {
  const trans = useSelector(translator);
  const [open, setOpen] = useState(false);
  // Summary checked by default; Detail unchecked.
  const [selected, setSelected] = useState([DASHBOARD_REPORT_TYPES.SUMMARY]);
  const [exporting, setExporting] = useState(false);

  const toggleItem = (key) => {
    setSelected((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const handleMainClick = async () => {
    if (disabled || exporting) return;
    setOpen(false);
    setExporting(true);
    // Fall back to Summary if somehow nothing is selected.
    const toExport = selected.length > 0 ? selected : [DASHBOARD_REPORT_TYPES.SUMMARY];
    try {
      for (const key of toExport) {
        await onExport(key);
      }
    } catch (error) {
      console.error('Error exporting dashboard to PDF:', error);
      message.error(trans('EXPORT_PDF_ERROR') || 'Failed to export PDF. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const handleChevronClick = (e) => {
    e.stopPropagation();
    if (!disabled && !exporting) setOpen((v) => !v);
  };

  const reportTypes = [
    { key: DASHBOARD_REPORT_TYPES.SUMMARY, label: trans('SUMMARY') || 'Summary' },
    { key: DASHBOARD_REPORT_TYPES.DETAIL, label: trans('DETAIL') || 'Detail' },
  ];

  const panel = (
    <div className="export-pdf-dropdown-panel">
      {reportTypes.map(({ key, label }) => (
        <div
          key={key}
          className={`export-pdf-item-row${selected.includes(key) ? ' selected' : ''}`}
          onClick={() => toggleItem(key)}
        >
          <span className="export-pdf-item-checkbox" />
          <span className="export-pdf-item-text">{label}</span>
        </div>
      ))}
    </div>
  );

  return (
    <Dropdown
      open={open && !disabled}
      onOpenChange={(v) => {
        if (!disabled && !exporting) setOpen(v);
      }}
      dropdownRender={() => panel}
      trigger={[]}
    >
      {/* Wrapper is the Dropdown anchor — split into two invisible click zones */}
      <div
        style={{
          position: 'relative',
          display: 'inline-flex',
          opacity: disabled || exporting ? 0.5 : 1,
        }}
      >
        {exporting ? (
          <div
            style={{
              width: 178,
              height: 48,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Spin size="small" />
          </div>
        ) : (
          <ExportPdfDropdownIcon />
        )}

        {/* Left zone — triggers immediate export */}
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: CHEVRON_X,
            height: '100%',
            cursor: disabled || exporting ? 'not-allowed' : 'pointer',
          }}
          onClick={handleMainClick}
        />

        {/* Right zone (chevron) — opens / closes the preference dropdown */}
        <div
          style={{
            position: 'absolute',
            left: CHEVRON_X,
            top: 0,
            right: 0,
            height: '100%',
            cursor: disabled || exporting ? 'not-allowed' : 'pointer',
          }}
          onClick={handleChevronClick}
        />
      </div>
    </Dropdown>
  );
};

DashboardExportButton.propTypes = {
  onExport: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default DashboardExportButton;
